import { FormatCurrencyOptions } from "@/hooks/useConfig/types";

import { NumberAbbreviator } from "./numberAbbreviator";

const DEFAULT_SETTINGS: CurrencySettings = { locale: "en-AU", currencyCode: "AUD" };

export interface CurrencySettings {
  locale: string;
  currencyCode: string;
}

export type FormatCurrency = typeof Currency.prototype.formatCurrency;

export class Currency {
  private numberAbbreviator: NumberAbbreviator;
  private currencyCode: string;
  private locale: string;

  constructor(numberAbbreviator: NumberAbbreviator, settings: CurrencySettings = DEFAULT_SETTINGS) {
    this.numberAbbreviator = numberAbbreviator;
    this.currencyCode = settings.currencyCode;
    this.locale = settings.locale;
  }

  formatCurrency = (
    amount: number,
    { abbreviate = false, minAbbreviatedRange = 1e6, ...restOptions }: FormatCurrencyOptions = {}
  ): string => {
    if (abbreviate) {
      return this.formatAbbreviatedAmount(amount, {
        minFormatRange: minAbbreviatedRange,
        ...restOptions,
      });
    } else {
      return this.formatAmount(amount, { ...restOptions });
    }
  };

  getSymbol(): string {
    const options: Intl.NumberFormatOptions = {
      style: "currency",
      currency: this.currencyCode,
      currencyDisplay: "symbol",
    } as const;

    const formatter = new Intl.NumberFormat(this.locale, options);
    const parts = formatter.formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency")?.value || "";
    return symbol;
  }

  private formatAmount(amount: number, options: Intl.NumberFormatOptions = {}): string {
    const defaultOptions: Intl.NumberFormatOptions = {
      style: "currency",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };
    const enforcedOptions: Intl.NumberFormatOptions = {
      currency: this.currencyCode,
    };
    const formatterOptions = { ...defaultOptions, ...options, ...enforcedOptions };

    const formatter = new Intl.NumberFormat(this.locale, formatterOptions);
    return formatter.format(amount);
  }

  private formatAbbreviatedAmount = (
    amount: number,
    {
      minFormatRange = 1e6,
      ...options
    }: Intl.NumberFormatOptions & { minFormatRange?: number } = {}
  ): string => {
    const defaultOptions: Intl.NumberFormatOptions = {
      style: "currency",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    };
    const enforcedOptions: Intl.NumberFormatOptions = {
      currency: this.currencyCode,
    };

    const combinedOptions = { minFormatRange, ...defaultOptions, ...options, ...enforcedOptions };
    return this.numberAbbreviator.formatAbbreviatedNumber(amount, combinedOptions);
  };
}
